import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';

import {
    buildLcdRegionSections,
    useLcdSubscriptionDictionary,
} from 'src/hooks/dictionaries/useLcdSubscriptionDictionary';

import {Popover} from '@pb/kit-component-dropdown';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';

import {
    userGroupsSearchResultLcdSubscriptionsDataSelector,
    userGroupsSearchResultLcdSubscriptionsResultSelector,
} from '../../slices/user-search-result-lcd-subscriptions.slice';

type Props = {
    userId: number,
    productTypeId: string,
};

const LcdSubscriptionsBaseCell = ({userId, productTypeId}: Props) => {
    const {isLoading, loadingError} = useSelector(userGroupsSearchResultLcdSubscriptionsDataSelector);
    const result = useSelector(userGroupsSearchResultLcdSubscriptionsResultSelector);
    const {
        lcdSubscriptionTrees, lcdLeafToFirstLevel, lcdRegionLabels, lcdFirstLevelOrder,
    } = useLcdSubscriptionDictionary();

    const regionSections = useMemo(
        () => buildLcdRegionSections(
            result?.data[userId],
            productTypeId,
            {
                trees: lcdSubscriptionTrees,
                leafToFirstLevel: lcdLeafToFirstLevel,
                regionLabels: lcdRegionLabels,
                firstLevelOrder: lcdFirstLevelOrder,
            },
        ),
        [result, userId, productTypeId, lcdSubscriptionTrees, lcdLeafToFirstLevel, lcdRegionLabels, lcdFirstLevelOrder],
    );

    if (isLoading || loadingError || regionSections === null) {
        return null;
    }

    if (!regionSections.length) {
        return <span>Not Subscribed</span>;
    }

    return (
        <Group alignItems="center" gap="xs">
            <span>Subscribed</span>
            <Popover
                hoverEnabled={true}
                placement="top-start"
                maxWidth={800}
                style={{width: 'max-content'}}
                anchor={({anchorRef, getAnchorProps}) => (
                    <span ref={anchorRef} {...getAnchorProps()}>
                        <Icon type={Icons.INFO}/>
                    </span>
                )}>
                <Group direction="h" gap="l">
                    {regionSections.map(region => (
                        <Group key={region.region} direction="v" gap="xs">
                            <Item style={{fontWeight: 600, whiteSpace: 'nowrap'}}>
                                {region.region}
                            </Item>
                            {region.firstLevelLabels.map((label, i) => (
                                <Item key={i} style={{whiteSpace: 'nowrap'}}>{label}</Item>
                            ))}
                        </Group>
                    ))}
                </Group>
            </Popover>
        </Group>
    );
};

export const LcdSubscriptionNewsCell = ({userId}: {userId: number}) => (
    <LcdSubscriptionsBaseCell userId={userId} productTypeId="NEWS"/>
);

export const LcdSubscriptionResearchCell = ({userId}: {userId: number}) => (
    <LcdSubscriptionsBaseCell userId={userId} productTypeId="RESEARCH"/>
);
